/**
 * tosijs-virta/core — everything except the web component. No DOM, no tosijs:
 * importable from Bun, Node, a worker, or an MCP server process.
 */

/**
 * A stable 64-bit fingerprint for a token, so a replica name can carry the
 * principal without carrying the secret itself (0.5.0 re-review 8, B1).
 * FNV-1a is a hash for naming, NOT a secret primitive — it is invertible
 * at 2^32 meet-in-the-middle against a known fingerprint, so it keys the
 * name but must not be treated as hiding the token (anyone who can read
 * the replica can read the token in storage anyway; the host must be the
 * authority). Requires a BigInt-capable runtime (ES2020+): the shipped
 * library's syntax floor.
 */
export const tokenFingerprint = (token: string): string => {
  let h = 0xcbf29ce484222325n
  for (let i = 0; i < token.length; i++) {
    h ^= BigInt(token.charCodeAt(i))
    h = (h * 0x100000001b3n) & 0xffffffffffffffffn
  }
  return h.toString(16).padStart(16, '0')
}

/**
 * The replica database name: keyed by the base (the host — plus the
 * author's `db` name when one is given) AND the principal (`s:<uid>` /
 * `t:<fingerprint>`), so a different credential opens a different database
 * by construction and can never replay another credential's cache — a
 * reload, a second tab and a fresh board included; the delete is hygiene,
 * not correctness. The name format is part of the public surface
 * (`tosijs-virta/core`): changing it orphans replicas, so do that as a
 * migration, not a rename. INJECTIVITY PRECONDITION: `principal` is the
 * name's LAST colon-delimited segment and must not itself contain `:`
 * beyond its prefix — a principal shape that adds a colon (an issuer, a
 * custom-token uid with `:`) must be escaped first or two distinct
 * principals can name one database.
 */
export const replicaDbName = (base: string, principal: string): string =>
  `virta:${base}:${principal}`

export * from './model'
export * from './tags'
export * from './palette'
export * from './tag-colors'
export * from './fold'
export * from './log'
export * from './indexeddb-log'
export * from './transcript'
export * from './markdown'
export * from './store'
export * from './verbs'
export * from './brief'
export * from './dispatch'
export * from './showcase'
export * from './files'
export * from './practices'
export * from './export'
export * from './guidance'
export * from './backup'
export * from './notices'
export * from './parents'
export * from './skills'
export * from './similar'
export * from './public'
export * from './news'
export * from './seed'
export * from './github'
export * from './notes'
export * from './remote'
export * from './firebase-auth'
export * from './cached-log'
export * from './mcp'
export * from './authorization'
export * from './manifest'
